<template>
  <div class="mfdw-wrapper">

    <!-- Title -->
    <p class="mfdw-subtitle">Set up your game's initial data</p>

    <!-- Step stepper -->
    <div class="step-stepper">
      <div
        v-for="(step, i) in wizardSteps"
        :key="step.id"
        class="step-stepper-item"
        :class="{ 'step-active': i === wizardStep, 'step-done': i < wizardStep }"
      >
        <div class="step-stepper-dot">{{ i + 1 }}</div>
        <div class="step-stepper-label">{{ step.title }}</div>
      </div>
    </div>

    <!-- Step content -->
    <div class="step-content">
      <h3 class="mfdw-step-prompt">{{ currentPrompt }}</h3>
      <p v-if="currentStep.hint" class="field-desc mfdw-step-hint">{{ currentStep.hint }}</p>

      <!-- string-list -->
      <template v-if="currentStep.type === 'string-list'">
        <div v-for="(item, i) in activeList" :key="i" class="mfdw-list-row">
          <input
            type="text"
            class="field-input"
            :value="item"
            :placeholder="currentStep.placeholder"
            @input="updateActiveItem(i, $event.target.value)"
          />
          <button type="button" class="mfdw-remove-btn" title="Remove" @click="removeActiveItem(i)">✕</button>
        </div>
        <button type="button" class="btn btn-secondary" style="margin-top:0.25rem;font-size:0.85rem" @click="addActiveItem">
          + {{ currentStep.addLabel }}
        </button>
      </template>

      <!-- unit-input -->
      <template v-else-if="currentStep.type === 'unit-input'">
        <div class="field-row" style="max-width:380px">
          <label class="field-label">Unit Name</label>
          <input
            v-model="firstUnitNameLocal"
            type="text"
            class="field-input"
            placeholder="e.g. Swordmen of the North"
          />
        </div>
      </template>

      <!-- single-input -->
      <template v-else-if="currentStep.type === 'single-input'">
        <div class="field-row" style="max-width:380px">
          <label class="field-label">{{ currentStep.label }}</label>
          <input
            v-model="singleInputValue"
            type="text"
            class="field-input"
            :placeholder="currentStep.placeholder"
          />
        </div>
        <div v-if="currentStep.hasDescription" class="field-row" style="max-width:380px;margin-top:0.75rem">
          <label class="field-label">Description</label>
          <textarea
            v-model="singleInputDescription"
            class="field-input"
            rows="6"
            :placeholder="currentStep.descriptionPlaceholder || 'Enter description...'"
          ></textarea>
        </div>
        <div v-if="currentStep.infoCards" class="mfdw-info-cards" style="margin-top:1rem">
          <div v-for="card in currentStep.infoCards" :key="card.label" class="mfdw-info-card">
            <span class="mfdw-info-icon">{{ card.icon }}</span>
            <div>
              <strong style="color:#e6edf3;font-size:0.92rem">{{ card.label }}</strong>
              <p class="field-desc" style="margin-top:0.15rem;margin-bottom:0">{{ card.description }}</p>
            </div>
          </div>
        </div>
      </template>

      <!-- statline-input -->
      <template v-else-if="currentStep.type === 'statline-input'">
        <div class="field-row" style="max-width:500px">
          <label class="field-label">Stats / Attributes</label>
          <p class="field-desc" style="margin-bottom:0.5rem">Define the stat keys and their default values (e.g. Move: 6, Attack: 4, Defense: 3)</p>
          <div v-for="(stat, i) in firstStatlineStatsLocal" :key="i" class="mfdw-stat-row">
            <input
              type="text"
              class="field-input"
              placeholder="Stat name (e.g. Move)"
              v-model="stat.key"
              style="flex: 1"
            />
            <input
              type="text"
              class="field-input"
              placeholder="Value (e.g. 6)"
              v-model="stat.value"
              style="flex: 1; margin-left: 0.5rem"
            />
            <button type="button" class="mfdw-remove-btn" title="Remove" @click="firstStatlineStatsLocal.splice(i, 1)">✕</button>
          </div>
          <button type="button" class="btn btn-secondary" style="margin-top:0.25rem;font-size:0.85rem" @click="firstStatlineStatsLocal.push({ key: '', value: '' })">
            + Add Stat
          </button>
        </div>
      </template>

      <!-- base-input -->
      <template v-else-if="currentStep.type === 'base-input'">
        <div class="field-row" style="max-width:380px">
          <label class="field-label">Shape</label>
          <select v-model.number="baseShapeLocal" class="field-input">
            <option :value="0">None</option>
            <option :value="1">Round</option>
            <option :value="2">Square</option>
          </select>
        </div>
        <div class="field-row" style="max-width:380px;margin-top:0.75rem">
          <label class="field-label">Width (mm)</label>
          <input
            v-model.number="baseWidthLocal"
            type="number"
            class="field-input"
            placeholder="e.g. 25"
            min="0"
          />
        </div>
        <div class="field-row" style="max-width:380px;margin-top:0.75rem">
          <label class="field-label">Length (mm)</label>
          <input
            v-model.number="baseLengthLocal"
            type="number"
            class="field-input"
            placeholder="e.g. 25"
            min="0"
          />
        </div>
      </template>

      <!-- hierarchy-input -->
      <template v-else-if="currentStep.type === 'hierarchy-input'">
        <div class="field-row" style="max-width:420px">
          <label class="field-label">Faction / Army Name <span style="color:#f85149">*</span></label>
          <input
            v-model="firstUnitFactionLocal"
            type="text"
            class="field-input"
            placeholder="e.g. The Empire, Orks, Undead Legion"
          />
        </div>
        <div class="field-row" style="max-width:420px;margin-top:0.75rem">
          <label class="field-label">Subsection <span style="color:#8b949e;font-weight:400">(optional)</span></label>
          <p class="field-desc" style="margin-bottom:0.3rem">e.g. Characters, Core, Battleline, Special Units</p>
          <input
            v-model="firstUnitSubsectionLocal"
            type="text"
            class="field-input"
            placeholder="e.g. Core"
          />
        </div>
      </template>

      <!-- completion -->
      <template v-else-if="currentStep.type === 'completion'">
        <div class="mfdw-completion">
          <p class="field-desc" style="margin-bottom:0.75rem;line-height:1.7">
            Let's choose a model to represent it. You can:
          </p>
          <ul class="mfdw-option-list">
            <li><strong>Scan your own models:</strong> Take a photo of your miniatures and we will convert it to 3D!</li>
            <li><strong>Link a 3D model</strong> file you have uploaded.</li>
            <li><strong>Use a model</strong> publicly available in the Warhall Catalogue.</li>
          </ul>
          <p class="field-desc" style="margin-top:0.85rem;margin-bottom:1.25rem">
            For our first model we will pick one from the Warhall Catalogue.
          </p>
          <div v-if="pickedSkin" class="mfdw-picked-skin">
            <img :src="pickedSkin.thumbnailUrl" :alt="pickedSkin.name" class="mfdw-picked-thumb" />
            <div>
              <p style="color:#3fb950;font-weight:600;margin:0 0 0.35rem">{{ pickedSkin.name }}</p>
              <button type="button" class="btn btn-secondary" style="font-size:0.82rem;padding:0.3rem 0.8rem" @click="$emit('pick-skin')">Change skin</button>
            </div>
          </div>
          <button v-else type="button" class="btn btn-primary" @click="$emit('pick-skin')">
            🎨 Choose model skin
          </button>
        </div>
      </template>
    </div>

    <!-- Navigation -->
    <div class="nav-buttons">
      <button class="btn btn-secondary" @click="prevOrBack">← Back</button>
      <div style="display:flex;gap:0.6rem;align-items:center">
        <button
          v-if="wizardStep < wizardSteps.length - 1"
          class="btn btn-primary"
          :disabled="!canProceed"
          @click="nextStep"
        >Next →</button>
        <button
          v-else
          class="btn btn-done"
          @click="finish"
        >{{ currentStep.type === 'completion' ? 'Finish ✓' : 'Done' }}</button>
      </div>
    </div>

  </div>
</template>

<script>
// ─── Step definitions ────────────────────────────────────────────────────────
// Supported types: 'string-list' | 'unit-input' | 'single-input' | 'statline-input' | 'base-input'
const WIZARD_STEPS = [
  {
    id: 'firstUnit',
    title: 'First Unit',
    type: 'unit-input',
    prompt: 'Lets add your first unit.\n What is the name of your first unit?',
  },
  {
    id: 'profiles',
    title: 'Unit Profiles',
    type: 'single-input',
    dataKey: 'profile',
    label: 'Profile Name',
    prompt: 'What profile compose this unit?',
    hint: 'A profile is a type of model within the unit. E.G: a unit of Swordsmen could contain a Swordman.',
    placeholder: 'e.g. Swordman',
    infoCards: [
      { label: 'Profiles are composed by rules and statlines.' },
      { icon: '📜', label: 'Rules',      description: 'Special rules and abilities that apply to the model.' },
      { icon: '📊', label: 'Statlines',  description: 'Numerical attributes like Movement, Attack, Toughness… Statlines can contain rules.' },
    ],
  },
  {
    id: 'firstRule',
    title: 'First Rule',
    type: 'single-input',
    dataKey: 'firstRule',
    label: 'Rule Name',
    prompt: 'Let\'s add a Rule to your {profile}',
    hint: 'Rules are special abilities or restrictions that apply to the profile.',
    placeholder: 'e.g. Shield Wall',
    hasDescription: true,
    descriptionPlaceholder: 'Describe what this rule does...',
  },
  {
    id: 'firstStatline',
    title: 'Stats',
    type: 'statline-input',
    prompt: 'Now let\'s add Stats to your {profile}',
    hint: 'Stats are numerical attributes like Movement, Attack, Defense…',
  },
  {
    id: 'base',
    title: 'Base',
    type: 'base-input',
    prompt: 'Most models have a base. What\'s the base for your {profile}?',
    hint: 'Define the shape and size of the base for this profile.',
  },
  {
    id: 'hierarchy',
    title: 'Army',
    type: 'hierarchy-input',
    prompt: 'What army does your unit "{unit}" belong to?',
    hint: 'This sets up the faction hierarchy used to organise units in your game.',
  },
  {
    id: 'completion',
    title: 'Model Skin',
    type: 'completion',
    prompt: 'Congratulations! Your unit\'s data is complete.',
  },
];

export default {
  name: 'MyFirstDataWizard',

  props: {
    factions:         { type: Array,  default: () => [] },
    sections:         { type: Array,  default: () => [] },
    firstUnitName:    { type: String, default: '' },
    firstUnitFaction: { type: String, default: '' },
    firstUnitSection: { type: String, default: '' },
    profileNames:     { type: Array,  default: () => [] },
    pickedSkin:       { type: Object, default: null },
  },

  data() {
    return {
      wizardStep: 0,
      wizardSteps: WIZARD_STEPS,
      // Local editable copies (initialized from props; re-synced on remount)
      factionsLocal:          this.factions.slice(),
      sectionsLocal:          this.sections.slice(),
      firstUnitNameLocal:     this.firstUnitName,
      firstUnitFactionLocal:  this.firstUnitFaction,
      firstUnitSectionLocal:  this.firstUnitSection,
      profileNamesLocal:      this.profileNames.slice(),
      firstRuleLocal:         '',
      firstRuleDescriptionLocal: '',
      firstStatlineStatsLocal: [{ key: '', value: '' }],
      baseShapeLocal:         0,  // 0 = None, 1 = Round, 2 = Square
      baseWidthLocal:         '',
      baseLengthLocal:        '',
      firstUnitFactionLocal:  '',
      firstUnitSubsectionLocal: '',
    };
  },

  computed: {
    currentStep() {
      return this.wizardSteps[this.wizardStep];
    },

    currentPrompt() {
      const profileName = (this.profileNamesLocal[0] || '').trim() || 'your profile';
      const unitName = this.firstUnitNameLocal.trim() || 'your unit';
      return (this.currentStep.prompt || '')
        .replace('{profile}', profileName)
        .replace('{unit}', unitName);
    },

    singleInputValue: {
      get() {
        switch (this.currentStep.dataKey) {
          case 'profile':        return this.profileNamesLocal[0] || '';
          case 'firstRule':      return this.firstRuleLocal;
          default:               return '';
        }
      },
      set(val) {
        switch (this.currentStep.dataKey) {
          case 'profile':        this.$set(this.profileNamesLocal, 0, val); break;
          case 'firstRule':      this.firstRuleLocal = val; break;
        }
      },
    },

    singleInputDescription: {
      get() {
        switch (this.currentStep.dataKey) {
          case 'firstRule':     return this.firstRuleDescriptionLocal;
          default:              return '';
        }
      },
      set(val) {
        switch (this.currentStep.dataKey) {
          case 'firstRule':     this.firstRuleDescriptionLocal = val; break;
        }
      },
    },

    canProceed() {
      switch (this.currentStep.id) {
        case 'firstUnit':
          return this.firstUnitNameLocal.trim().length > 0;
        case 'profiles':
          return (this.profileNamesLocal[0] || '').trim().length > 0;
        case 'firstRule':
          return this.firstRuleLocal.trim().length > 0;
        case 'firstStatline':
          // At least one stat with both key and value filled
          return this.firstStatlineStatsLocal.some(stat => stat.key.trim() && stat.value.trim());
        case 'base':
          return true; // Base is optional
        case 'hierarchy':
          return this.firstUnitFactionLocal.trim().length > 0;
        default:
          return true;
      }
    },

    activeList() {
      return [];
    },

    validFactions() { return this.factionsLocal.filter(f => f.trim()); },
    validSections()  { return this.sectionsLocal.filter(s => s.trim()); },
  },

  methods: {
    // ── List helpers ────────────────────────────────────────────
    updateActiveItem(i, val) {
      this.$set(this.activeList, i, val);
    },
    addActiveItem() {
      this.activeList.push('');
    },
    removeActiveItem(i) {
      this.activeList.splice(i, 1);
    },

    // ── Navigation ───────────────────────────────────────────────
    prevOrBack() {
      if (this.wizardStep > 0) {
        this.wizardStep--;
      } else {
        this.$emit('back');
      }
    },
    nextStep() {
      if (this.wizardStep < this.wizardSteps.length - 1) {
        this.wizardStep++;
      }
    },
    finish() {
      // Convert stats array to object { Move: '6', Attack: '4', ... }
      const statsObj = {};
      this.firstStatlineStatsLocal.forEach(stat => {
        if (stat.key.trim()) {
          statsObj[stat.key.trim()] = stat.value.trim();
        }
      });

      this.$emit('done', {
        firstUnitName:        this.firstUnitNameLocal,
        profileNames:         this.profileNamesLocal.filter(p => p.trim()),
        firstRule:            this.firstRuleLocal.trim(),
        firstRuleDescription: this.firstRuleDescriptionLocal.trim(),
        firstStatlineStats:   statsObj,
        baseShape:            this.baseShapeLocal,
        baseWidth:            this.baseWidthLocal,
        baseLength:           this.baseLengthLocal,
        firstUnitFaction:     this.firstUnitFactionLocal.trim(),
        firstUnitSection:     this.firstUnitSubsectionLocal.trim(),
      });
    },
  },
};
</script>

<style scoped>
.mfdw-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

/* ── Step stepper (matches GameConfigWizard) ── */
.step-stepper {
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.8rem;
  overflow-x: auto;
  padding-bottom: 0.2rem;
}
.step-stepper-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 70px;
  position: relative;
}
.step-stepper-item:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 13px;
  left: calc(50% + 16px);
  right: calc(-50% + 16px);
  height: 2px;
  background: #30363d;
}
.step-stepper-item.step-done:not(:last-child)::after { background: #2563eb; }
.step-stepper-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #21262d;
  border: 2px solid #30363d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #8b949e;
  z-index: 1;
  flex-shrink: 0;
}
.step-stepper-item.step-active .step-stepper-dot { background: #2563eb; border-color: #2563eb; color: #fff; }
.step-stepper-item.step-done  .step-stepper-dot { background: #0d2a0d; border-color: #2ecc71; color: #2ecc71; }
.step-stepper-label {
  font-size: 0.68rem;
  color: #555;
  margin-top: 0.35rem;
  text-align: center;
  word-break: break-word;
  max-width: 88px;
  line-height: 1.3;
}
.step-stepper-item.step-active .step-stepper-label { color: #58a6ff; font-weight: 600; }
.step-stepper-item.step-done  .step-stepper-label { color: #2ecc71; }

/* ── Shared field styles ── */
.step-content { min-height: 320px; }
.field-row { display: flex; flex-direction: column; gap: 0.3rem; }
.field-label { font-weight: 600; font-size: 0.9rem; color: #c0c0d0; }
.field-desc  { font-size: 0.78rem; color: #7a8aaa; line-height: 1.4; margin: 0; }
.field-input {
  background: #21262d;
  border: 1px solid #30363d;
  color: #e0e0e0;
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;
}
.field-input:focus { border-color: #58a6ff; }
textarea.field-input {
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  line-height: 1.4;
}

/* ── Navigation ── */
.nav-buttons { display: flex; justify-content: space-between; align-items: center; margin-top: 2rem; padding-top: 1rem; border-top: 1px solid #30363d; gap: 0.5rem; }

.mfdw-subtitle {
  font-size: 0.88rem;
  color: #8b949e;
  margin: 0;
  text-align: center;
}

.mfdw-step-prompt {
  font-size: 1rem;
  font-weight: 600;
  color: #e6edf3;
  margin: 0 0 0.3rem;
}

.mfdw-step-hint {
  margin-bottom: 0.75rem;
}

/* string-list */
.mfdw-list-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}
.mfdw-list-row .field-input {
  flex: 1;
}
.mfdw-remove-btn {
  background: none;
  border: none;
  color: #f85149;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0.25rem 0.4rem;
  border-radius: 4px;
  flex-shrink: 0;
  line-height: 1;
}
.mfdw-remove-btn:hover {
  background: rgba(248, 81, 73, 0.15);
}

/* informational */
.mfdw-info-cards {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.mfdw-info-card {
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 8px;
  padding: 0.75rem 1rem;
}
.mfdw-info-icon {
  font-size: 1.5rem;
  line-height: 1;
  flex-shrink: 0;
}

/* statline stats */
.mfdw-stat-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}
.mfdw-stat-row .field-input {
  flex: 1;
}

/* button disabled state */
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* completion step */
.mfdw-completion { display: flex; flex-direction: column; gap: 0.5rem; }
.mfdw-option-list { color: #94a3b8; font-size: 0.85rem; margin: 0; padding-left: 1.3rem; line-height: 1.9; }
.mfdw-option-list li strong { color: #c0c0d0; }
.mfdw-picked-skin { display: flex; align-items: center; gap: 1rem; background: #161b22; border: 1px solid #2ecc71; border-radius: 8px; padding: 0.75rem 1rem; }
.mfdw-picked-thumb { width: 80px; height: 80px; border-radius: 6px; object-fit: cover; border: 2px solid #2ecc71; flex-shrink: 0; }
</style>
